import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  FormGroup,
  Typography,
  Grid,
  Box,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { getIconComponent } from '../utils/iconMapping';

const defaultForm = {
  name: '',
  friendlyName: '',
  icon: '',
  isLocked: false,
  supportsOnOff: true,
  supportsDim: false,
  supportsColor: false
};

const OutputTypeDialog = ({ open, onClose, outputType, onSave, saving, iconMappings = [], t }) => {
  const [form, setForm] = useState(defaultForm);
  const isEditing = !!outputType;

  useEffect(() => {
    if (outputType) {
      setForm({
        name: outputType.name || '',
        friendlyName: outputType.friendlyName || '',
        icon: outputType.icon || '',
        isLocked: outputType.isLocked || false,
        supportsOnOff: outputType.supportsOnOff !== false,
        supportsDim: outputType.supportsDim || false,
        supportsColor: outputType.supportsColor || false
      });
    } else {
      setForm({ ...defaultForm, icon: iconMappings[0]?.key || '' });
    }
  }, [outputType, open, iconMappings]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleIconSelect = (iconKey) => {
    setForm(prev => ({ ...prev, icon: iconKey }));
  };

  const handleSubmit = () => {
    onSave(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing
          ? (t ? t('QbusOutputTypes.edit') : 'Edit Output Type')
          : (t ? t('QbusOutputTypes.add') : 'Add Output Type')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t ? t('QbusOutputTypes.name') : 'Name'}
              value={form.name}
              onChange={handleChange('name')}
              helperText={t ? t('QbusOutputTypes.name_help') : 'Internal identifier (e.g., relay, dimmer)'}
              disabled={isEditing}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t ? t('QbusOutputTypes.friendly_name') : 'Friendly Name'}
              value={form.friendlyName}
              onChange={handleChange('friendlyName')}
              helperText={t ? t('QbusOutputTypes.friendly_name_help') : 'Display name shown to users'}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              {t ? t('QbusOutputTypes.icon') : 'Icon'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {iconMappings.map((mapping) => {
                const Icon = getIconComponent(mapping.key);
                return (
                  <Tooltip key={mapping.id} title={mapping.label}>
                    <IconButton
                      onClick={() => handleIconSelect(mapping.key)}
                      sx={{
                        border: 2,
                        borderColor: form.icon === mapping.key ? 'primary.main' : 'transparent',
                        bgcolor: form.icon === mapping.key ? 'action.selected' : 'action.hover',
                        borderRadius: 1,
                        width: 48,
                        height: 48,
                        '&:hover': {
                          borderColor: form.icon === mapping.key ? 'primary.main' : 'action.disabled'
                        }
                      }}
                    >
                      <Icon color={form.icon === mapping.key ? 'primary' : 'action'} />
                    </IconButton>
                  </Tooltip>
                );
              })}
              {iconMappings.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t ? t('QbusOutputTypes.no_icon_mappings') : 'No icon mappings configured'}
                </Typography>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t ? t('QbusOutputTypes.icon_help') : 'Select an icon for this output type'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              {t ? t('QbusOutputTypes.capabilities') : 'Capabilities'}
            </Typography>
            <FormGroup row>
              <FormControlLabel
                control={<Switch checked={form.supportsOnOff} onChange={handleChange('supportsOnOff')} />}
                label={t ? t('QbusOutputTypes.supports_on_off') : 'On/Off'}
              />
              <FormControlLabel
                control={<Switch checked={form.supportsDim} onChange={handleChange('supportsDim')} />}
                label={t ? t('QbusOutputTypes.supports_dim') : 'Dimming'}
              />
              <FormControlLabel
                control={<Switch checked={form.supportsColor} onChange={handleChange('supportsColor')} />}
                label={t ? t('QbusOutputTypes.supports_color') : 'RGB Color'}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={form.isLocked} onChange={handleChange('isLocked')} color="error" />}
              label={
                <Box>
                  <Typography variant="body2">
                    {t ? t('QbusOutputTypes.is_locked') : 'Locked (Admin-only)'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t ? t('QbusOutputTypes.is_locked_help') : 'Enable for security-sensitive outputs (doors, gates, alarms)'}
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t ? t('Common.cancel') : 'Cancel'}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={24} /> : (t ? t('Common.save') : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OutputTypeDialog;
